/* eslint-disable @typescript-eslint/no-explicit-any */
import { GamoraArgsType, GamoraConstructorType } from '../generic.js';
import {
  GamoraFoundationSuperNode,
  GamoraFoundationSuperNodeArgs,
} from './super.js';
import { assert } from '../../utils/assert.js';
import { GamoraUtilsStrand } from '../../utils/strand.js';

/**
 * GamoraFoundationNodeChildMapEntry
 *
 * A nodemap entry
 * @public
 */
export type GamoraFoundationNodeChildMapEntry = GamoraFoundationNodeConstructor;

/**
 * GamoraFoundationNodeChildMap
 *
 * A record of all nodes of a base type
 * @public
 */
export type GamoraFoundationNodeChildMap<TSelf> = {
  [nodeName in keyof TSelf]: GamoraFoundationNodeChildMapEntry;
};

/**
 * GamoraFoundationNodeRoot
 * @internal
 */
export type GamoraFoundationNodeRoot = GamoraFoundationNode<any, any, any>;

/**
 * GamoraFoundationTreeBaseConstructor
 *
 * A constructor for a base type
 * @internal
 */
export type GamoraFoundationNodeConstructor = GamoraConstructorType<
  GamoraFoundationNode<any, any, any>
>;

/**
 * GamoraFoundationNodeArgsType
 *
 * Used internally to obtain the arguments of a node map entry
 * @internal
 */
export type GamoraFoundationNodeArgsType<
  TArgsType extends GamoraFoundationNodeConstructor,
> = GamoraArgsType<TArgsType>;

/**
 * GamoraFoundationNodeChildMapArgs
 * @public
 */
export type GamoraFoundationNodeChildMapArgs<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
> = {
  readonly [k in keyof TChildMap]?: readonly [
    TChildMap[k],
    GamoraFoundationNodeArgsType<TChildMap[k]>,
  ];
};

/**
 * GamoraFoundationNodeArgs
 *
 * Mandatory node arguments
 * @internal
 */
export interface GamoraFoundationNodeArgs<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap> = never,
> extends GamoraFoundationSuperNodeArgs {
  root?: GamoraFoundationNodeRoot;
  childMap?: GamoraFoundationNodeChildMapArgs<TChildMap>;
}

/**
 * GamoraFoundationNode
 *
 * Abstract class that represents a node
 * @class
 * @public
 */
export abstract class GamoraFoundationNode<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
  TRoot extends GamoraFoundationNodeRoot = any,
  TSuper extends GamoraFoundationNodeRoot = any,
> extends GamoraFoundationSuperNode<TSuper> {
  private _$root: GamoraFoundationNodeRoot | undefined;
  protected _$strand: GamoraUtilsStrand;
  protected _$children: Map<
    keyof TChildMap,
    GamoraFoundationNode<any, any, any>
  >;
  constructor(args: GamoraFoundationNodeArgs<TChildMap>) {
    super(args);
    this._$root = args.root;
    this._$children = new Map();
    this._$strand = new GamoraUtilsStrand();

    // Add nodes
    if (args.childMap) {
      for (const key in args.childMap) {
        const value = args.childMap[key];
        if (value) {
          const [ctor, ctorArgs] = value;
          this._$addChild(key, ctor, ctorArgs);
        }
      }
    }

    // Start child strands when this strand starts
    this.$strand.post(async () => {
      for (const child of this._$children.values()) {
        if (!child.$strand.started) {
          child.$strand.start();
        }
      }
    });
  }

  /**
   * Expose strand
   */
  get $strand() {
    return this._$strand;
  }

  /**
   * Gets root
   */
  get $root() {
    assert(this._$root !== undefined, '$root is not set.');
    return this._$root as TRoot;
  }

  /**
   * Add node
   */
  protected _$addChild<K extends keyof TChildMap>(
    key: K,
    ctor: TChildMap[K],
    args: GamoraArgsType<TChildMap[K]>,
  ): InstanceType<TChildMap[K]> {
    assert(
      typeof key === 'string',
      'Invalid key argument: not a valid string.',
    );
    assert(
      typeof ctor === 'function' &&
        ctor.prototype instanceof GamoraFoundationNode,
      'Invalid key argument: Not a GamoraFoundationNode.',
    );
    assert(
      !this._$children.has(key),
      'Invalid key argument: node already exists.',
    );
    const node = new ctor({
      root: this._$root ? this._$root : this,
      ...args,
    });
    this._$children.set(key, node);
    return node as InstanceType<TChildMap[K]>;
  }

  /**
   * Get node
   */
  $<K extends keyof TChildMap>(key: K): InstanceType<TChildMap[K]> {
    return this.$getChild(key);
  }

  /**
   * Get node
   */
  $getChild<K extends keyof TChildMap>(key: K): InstanceType<TChildMap[K]> {
    assert(
      typeof key === 'string',
      'Invalid key argument: not a valid string.',
    );
    const node = this._$children.get(key);
    assert(node !== undefined, 'Invalid key argument: node does not exist.');
    return node as InstanceType<TChildMap[K]>;
  }
}
